import React from 'react';

import { BrokerCompanySuggestionType } from 'constants/BrokerCompanySuggestionType';
import CompanySearchCombo from 'components/CreateComps/CompanySearchCombo';
import FormControl from 'components/FormControl';
import TextInput from 'components/Inputs/TextInput';
import { IRadioItem } from 'components/Inputs/RadioInput';
import { IBrokerInput } from 'interfaces/inputs/IBrokerInput';
import { IdName } from 'interfaces/IdName';
import { ICompany } from 'interfaces/ICompany';

import { BrokerOptions } from '../BrokerOptions';
import styles from '../../FormSection.module.scss';

interface Props {
  id: string;
  marketId: number;
  propertyTypeId: number;
  isMultiSelect: boolean;
  fieldName: string;
  type: BrokerCompanySuggestionType;
  brokers: IBrokerInput[];
  labels: {
    company: string;
    companyBtn: string;
    companyPlaceholder: string;
    contact: string;
    contactPlaceholder: string;
  };
  onChange: (field: string, value: any) => void;
}

export const BrokerAndContact = ({
  id,
  marketId,
  propertyTypeId,
  isMultiSelect,
  fieldName,
  brokers = [],
  type,
  onChange,
  labels,
}: Props) => {
  const companies = brokers?.map(b => ({ id: b.id, name: b?.company }));

  const selectedItem = isMultiSelect
    ? brokers?.map(b => b?.company ?? '')
    : brokers?.[0]?.company ?? '';

  const onSingleSelect = (value: ICompany) => {
    const newBrokers = value ? [{ id: value.id, company: value.name }] : null;
    onChange(fieldName, newBrokers);
  };

  const onMultiSelect = (value: IdName): void => {
    const isAlreadySelected = brokers?.some(b => b.id === value.id);
    if (isAlreadySelected) return;

    onChange(fieldName, [
      ...(brokers || []),
      { id: value.id, company: value.name },
    ]);
  };

  const onRemoveItem = (value: IdName): void => {
    return onChange(
      fieldName,
      brokers.filter(b => b.id !== value.id),
    );
  };

  const onClearSelectedSuggestion = !isMultiSelect
    ? () => onChange(fieldName, [])
    : undefined;

  const onOptionChange = (item: IRadioItem) => {
    const newBroker = { id: item.id, company: item.value };

    if (!isMultiSelect) {
      onChange(fieldName, [newBroker]);
      return;
    }

    const isAlreadySelected = brokers.some(b => b.id === newBroker.id);
    const updatedBrokers = isAlreadySelected
      ? brokers.filter(b => b.id !== newBroker.id)
      : [...brokers, newBroker];

    onChange(fieldName, updatedBrokers);
  };

  const updateBrokerNameAtIndex = (
    brokers: IBrokerInput[],
    index: number,
    name: string | null,
  ): IBrokerInput[] =>
    brokers.map((b, i) => (i === index ? { ...b, name: name ?? '' } : b));

  return (
    <>
      <CompanySearchCombo
        btnLabel={labels.companyBtn}
        label={labels.company}
        inputId={id}
        placeholder={labels.companyPlaceholder}
        wrapperClassName={styles['form-row']}
        companies={companies}
        isMultiple={isMultiSelect}
        aliasPlaceholder={(suggestionName: string) =>
          `Click to tag ${suggestionName} as a Broker.`
        }
        onCompanySelection={isMultiSelect ? onMultiSelect : onSingleSelect}
        onClearSelectedSuggestion={onClearSelectedSuggestion}
        onRemoveItem={onRemoveItem}
      />

      <FormControl indented>
        <BrokerOptions
          selectedItem={selectedItem}
          onChange={onOptionChange}
          isMultiSelect={isMultiSelect}
          brokerCompanySuggestionType={type}
          marketId={marketId}
          propertyTypeId={propertyTypeId}
        />
      </FormControl>
      {brokers?.map((broker, index) => (
        <FormControl
          key={broker.company}
          label={
            isMultiSelect ? `${broker.company} Broker Contact` : labels.contact
          }
          wrapperClassName={styles['form-row']}
        >
          <TextInput
            fullWidth
            value={broker.name}
            inputClassName={styles['source-input']}
            placeholder={labels.contactPlaceholder}
            isLarge
            onChange={item => {
              const updatedBrokers = updateBrokerNameAtIndex(
                brokers,
                index,
                item,
              );
              onChange(fieldName, updatedBrokers);
            }}
          />
        </FormControl>
      ))}
    </>
  );
};
